import React, { useRef, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { CaretDownIcon, OfficeIcon } from '@modernloop/shared/icons';
import { Button, Menu, Typography } from '@mui/material';

import OrganizationSwitcherItems from './OrganizationSwitcherItems';
import { useOrganizationMemberships } from './useOrganizationMemberships';

/**
 * A standalone Organization switcher: a button that opens the switcher rows in its own menu.
 *
 * The onboarding shell (`OrgSettingsLayout`, shown to a user whose Organization holds no directory
 * record for them) has no account menu, so without this control a user who switches into such an
 * Organization has no way back out. The account menu does not use this button. It opens the same
 * rows from its own "Switch tenant" row (`AccountMenuSwitchTenantItem`).
 *
 * Renders nothing for a user with one Membership.
 */
const OrganizationSwitcherButton = (): JSX.Element | null => {
  const { t } = useTranslation();
  const memberships = useOrganizationMemberships();
  const [showMenu, setShowMenu] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  if (memberships.length < 2) {
    return null;
  }

  const activeOrganizationName = memberships.find((membership) => membership.isActive)?.organization.name ?? '';

  return (
    <>
      <Button
        data-testid="organization-switcher-button"
        ref={buttonRef}
        fullWidth
        variant="text"
        size="small"
        startIcon={<OfficeIcon />}
        endIcon={<CaretDownIcon />}
        onClick={() => setShowMenu(true)}
        sx={{ justifyContent: 'flex-start', overflow: 'hidden', textAlign: 'left' }}
      >
        <Typography variant="body2" fontWeight={500} noWrap width="100%">
          {activeOrganizationName || t('hiring-dashboard-nav:nav.organizationSwitcher.switchTenant')}
        </Typography>
      </Button>
      {showMenu && (
        <Menu
          anchorEl={buttonRef.current}
          open
          onClose={() => setShowMenu(false)}
          slotProps={{ paper: { sx: { minWidth: 248 } } }}
        >
          <OrganizationSwitcherItems onSelect={() => setShowMenu(false)} />
        </Menu>
      )}
    </>
  );
};

export default OrganizationSwitcherButton;
